import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

const AUTH_KEY = "tj13_basic";

export const getAuthToken = () => localStorage.getItem(AUTH_KEY);

export const setAuthToken = (user, password) => {
  localStorage.setItem(AUTH_KEY, btoa(`${user}:${password}`));
};

export const clearAuthToken = () => localStorage.removeItem(AUTH_KEY);

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Basic ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearAuthToken();
      window.dispatchEvent(new Event("tj13-unauthorized"));
    }
    return Promise.reject(err);
  }
);

export const downloadCsv = async (path, params, filename) => {
  const { data } = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const verifyAuth = () => api.get("/auth/verify");

export const NETWORKS = [
  { id: "ethereum", label: "Ethereum", short: "ETH" },
  { id: "base", label: "Base", short: "BASE" },
  { id: "arbitrum", label: "Arbitrum", short: "ARB" },
  { id: "solana", label: "Solana", short: "SOL" },
];

export const PORTFOLIO_KINDS = [
  { id: "onchain", label: "On-chain Wallet" },
  { id: "exchange", label: "Exchange (CEX)" },
  { id: "broker", label: "Broker (e.g. IBKR)" },
  { id: "other", label: "Other" },
];

export const ASSETS = [
  "BTC", "WBTC", "cbBTC", "ETH", "WETH", "wstETH", "SOL",
  "ARB", "OP", "LINK", "UNI", "AAVE", "USDC", "USDT", "DAI", "JUP",
];

export const STRATEGY_TAGS = [
  "Swing", "Scalp", "Trend Following", "Mean Reversion", "Narrative",
  "Breakout", "DCA", "Hedge", "Airdrop Farm", "Arbitrage",
];

// Immutable internal slugs; labels live in the i18n dictionaries under why.buy.* / why.sell.*
export const WHY_BUY = [
  { key: "signal", i18nKey: "why.buy.signal", icon: "Broadcast" },
  { key: "fomo", i18nKey: "why.buy.fomo", icon: "TrendUp" },
  { key: "dip", i18nKey: "why.buy.dip", icon: "TrendDown" },
  { key: "intuition", i18nKey: "why.buy.intuition", icon: "Sparkle" },
];

export const WHY_SELL = [
  { key: "signal", i18nKey: "why.sell.signal", icon: "Broadcast" },
  { key: "high_price", i18nKey: "why.sell.high_price", icon: "Crown" },
  { key: "panic", i18nKey: "why.sell.panic", icon: "TrendDown" },
  { key: "intuition", i18nKey: "why.sell.intuition", icon: "Sparkle" },
];

export const PORTFOLIO_KIND_IDS = ["onchain", "exchange", "broker"];

// Basket / fund (LP token) portfolios are fully implemented but hidden from the
// creation form. Flip to true to offer the type again.
export const BASKET_PORTFOLIO_ENABLED = false;

export const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", BRL: "R$", GBP: "£" };

const money = { code: "USD", rate: 1, symbol: "$" };

// Locale drives every Intl formatter below; i18n sets it on language change.
const display = { locale: "en-US" };

export const setLocale = (lng) => {
  display.locale = lng && lng.startsWith("ru") ? "ru-RU" : "en-US";
};

export const getLocale = () => display.locale;

export const setCurrency = (code, rate) => {
  money.code = code;
  money.rate = rate || 1;
  money.symbol = CURRENCY_SYMBOLS[code] || "$";
};

export const getCurrency = () => ({ ...money });

const currencyFmt = (digits) =>
  new Intl.NumberFormat(display.locale, {
    style: "currency",
    currency: money.code,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const fmtUsd = (n, digits = 2) =>
  !Number.isFinite(Number(n)) ? "—" : currencyFmt(digits).format(Number(n) * money.rate);

export const fmtSigned = (n) =>
  !Number.isFinite(Number(n))
    ? "—"
    : `${n >= 0 ? "+" : "-"}${currencyFmt(2).format(Math.abs(Number(n) * money.rate))}`;

// Safe .toFixed: never throws on null / undefined / NaN coming from the API or CSV rows.
export const fixed = (n, digits = 2, fallback = "0.00") =>
  Number.isFinite(Number(n)) ? Number(n).toFixed(digits) : fallback;

export const fmtNum = (n, digits = 2) =>
  !Number.isFinite(Number(n))
    ? "—"
    : new Intl.NumberFormat(display.locale, { maximumFractionDigits: digits }).format(Number(n));

export const fmtDate = (value, withTime = false) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return new Intl.DateTimeFormat(display.locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(d);
};

export const shortHash = (h) => (h ? `${h.slice(0, 6)}…${h.slice(-4)}` : "—");
